import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
    EuiFlexItem,
    EuiFlexGroup,
    EuiText,
    EuiSpacer,
    EuiButton,
    EuiButtonEmpty,
    EuiButtonIcon,
    EuiFlyout,
    EuiFlyoutBody,
    EuiFlyoutFooter,
    EuiFlyoutHeader,
    EuiTitle,
    EuiCallOut,
    EuiHorizontalRule,
    EuiCheckbox,
    EuiLoadingSpinner,
    EuiBadge,
    EuiComboBox,
    EuiFieldSearch,
} from '@elastic/eui';
import APIV3 from '../../../../api/axios/APIV3';

const CategoryItemSelectionFlyout = ({
    isOpen,
    onClose,
    mode,
    outletDataRef,
}) => {
    const languageId = useSelector((state) => state.language.languageId);
    const outlets = useSelector((state) => state.outlet.outletList);
    const { setValue, watch } = useFormContext();
    const [, forceRender] = useState(0); // trigger re-render when ref data changes

    // Store outlet selection in form state so it persists across flyout open/close
    const selectedOutlets = watch('_flyout_selected_outlets') || [];
    const activeOutletId = watch('_flyout_active_outlet_id') || null;

    const setSelectedOutlets = useCallback(
        (outlets) => setValue('_flyout_selected_outlets', outlets),
        [setValue]
    );
    const setActiveOutletId = useCallback(
        (id) => setValue('_flyout_active_outlet_id', id),
        [setValue]
    );

    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [isLoadingAllData, setIsLoadingAllData] = useState(false);

    // Local search
    const [searchQuery, setSearchQuery] = useState('');

    // Items loading state
    const [loadingItemsFor, setLoadingItemsFor] = useState({});
    const [loadingVariantsFor, setLoadingVariantsFor] = useState({});

    const outletOptions = outlets
        .filter((outlet) => outlet?.inputDisplay)
        .map((outlet) => ({
            label: outlet.inputDisplay,
            value: outlet.value,
        }));


    // Get current outlet's cached data
    const getOutletData = (outletId) => {
        if (!outletId) return { tree: [], categoryItems: {}, itemVariants: {}, expandedIds: {} };
        return outletDataRef.current[outletId] || { tree: [], categoryItems: {}, itemVariants: {}, expandedIds: {} };
    };

    const setOutletData = (outletId, updater) => {
        const current = getOutletData(outletId);
        outletDataRef.current[outletId] = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
        forceRender((n) => n + 1);
    };

    // Build tree from flat category list using parent_id
    const buildCategoryTree = (flatCategories) => {
        const parentCategories = flatCategories.filter((c) => c.parent_id === null);
        const subCategories = flatCategories.filter((c) => c.parent_id !== null);

        return parentCategories.map((parent) => ({
            ...parent,
            sub_categories: subCategories.filter((sub) => sub.parent_id === parent.id),
        }));
    };

    // Fetch ALL data for an outlet: items for all categories, then variants for all items
    const fetchAllDataForOutlet = useCallback((outletId, tree, onComplete) => {
        // Step 1: Collect all category IDs (parents + subcategories)
        const allCategoryIds = [];
        tree.forEach((cat) => {
            allCategoryIds.push(cat.id);
            (cat.sub_categories || []).forEach((sub) => {
                allCategoryIds.push(sub.id);
            });
        });

        // Step 2: Fetch items for ALL categories in parallel
        const itemPromises = allCategoryIds.map((catId) =>
            APIV3.get(`/restaurants/:restaurantId/outlets/${outletId}/categories/${catId}/items/stock`)
                .then((response) => ({
                    categoryId: catId,
                    items: response.success ? response.items || [] : [],
                }))
                .catch(() => ({ categoryId: catId, items: [] }))
        );

        Promise.all(itemPromises).then((itemResults) => {
            const categoryItems = {};
            itemResults.forEach(({ categoryId, items }) => {
                categoryItems[categoryId] = items;
            });

            // Update items immediately so tree renders
            setOutletData(outletId, (prev) => ({
                ...prev,
                categoryItems: { ...prev.categoryItems, ...categoryItems },
            }));

            // Step 3: Collect all items that have variants
            const itemsWithVariants = [];
            Object.values(categoryItems).forEach((items) => {
                items.forEach((item) => {
                    if (item.variants_count > 0) {
                        itemsWithVariants.push(item.id);
                    }
                });
            });

            if (itemsWithVariants.length === 0) {
                if (onComplete) onComplete();
                return;
            }

            // Step 4: Fetch variants for ALL items with variants in parallel
            const variantPromises = itemsWithVariants.map((itemId) =>
                APIV3.get(`/restaurants/:restaurantId/outlets/${outletId}/items/${itemId}/variant-options/stock`)
                    .then((response) => ({
                        itemId,
                        variants: response.success ? response.variant_options || [] : [],
                    }))
                    .catch(() => ({ itemId, variants: [] }))
            );

            Promise.all(variantPromises).then((variantResults) => {
                const itemVariants = {};
                variantResults.forEach(({ itemId, variants }) => {
                    itemVariants[itemId] = variants;
                });

                setOutletData(outletId, (prev) => ({
                    ...prev,
                    itemVariants: { ...prev.itemVariants, ...itemVariants },
                }));

                if (onComplete) onComplete();
            });
        });
    }, []);

    // Fetch categories for a specific outlet
    const fetchCategoriesForOutlet = useCallback((outletId) => {
        if (outletDataRef.current[outletId]?.tree?.length > 0) return; // already fetched

        setIsLoadingCategories(true);
        setIsLoadingAllData(true);
        APIV3.get(`/restaurants/:restaurantId/outlets/${outletId}/categories/stock`)
            .then((response) => {
                if (response.success) {
                    const tree = buildCategoryTree(response.categories || []);
                    // Start with all accordions closed
                    setOutletData(outletId, { tree, expandedIds: {}, categoryItems: {}, itemVariants: {} });

                    // Pre-fetch ALL items and variants for complete search
                    fetchAllDataForOutlet(outletId, tree, () => setIsLoadingAllData(false));
                } else {
                    setIsLoadingAllData(false);
                }
            })
            .catch(() => setIsLoadingAllData(false))
            .finally(() => setIsLoadingCategories(false));
    }, [fetchAllDataForOutlet]);

    // When outlet selection changes
    const handleOutletChange = (selectedOptions) => {
        setSelectedOutlets(selectedOptions);

        if (selectedOptions.length > 0) {
            const lastSelected = selectedOptions[selectedOptions.length - 1];
            setActiveOutletId(lastSelected.value);
            fetchCategoriesForOutlet(lastSelected.value);
        } else {
            setActiveOutletId(null);
        }
    };

    // When clicking an outlet pill
    const handleOutletPillClick = (outletId) => {
        setActiveOutletId(outletId);
        setSearchQuery('');
        fetchCategoriesForOutlet(outletId);
    };

    // Remove outlet from selection
    const handleRemoveOutlet = (outletId) => {
        const updated = selectedOutlets.filter((o) => o.value !== outletId);
        setSelectedOutlets(updated);
        // Clean up cached data
        delete outletDataRef.current[outletId];

        if (activeOutletId === outletId) {
            setActiveOutletId(updated.length > 0 ? updated[0].value : null);
        }
        forceRender((n) => n + 1);
    };

    // On mount: auto-select outlets from linked_outlets or restore previous selection
    useEffect(() => {
        if (selectedOutlets.length > 0 && activeOutletId) {
            // Already have outlets selected (form state persisted) — just ensure data is fetched
            fetchCategoriesForOutlet(activeOutletId);
            return;
        }

        // Try to auto-select from linked_outlets (edit mode)
        const linkedOutletIds = watch('_flyout_linked_outlets') || [];
        if (linkedOutletIds.length > 0) {
            const matchedOutlets = linkedOutletIds
                .map((outletId) => outletOptions.find((o) => o.value === outletId))
                .filter(Boolean);

            if (matchedOutlets.length > 0) {
                setSelectedOutlets(matchedOutlets);
                setActiveOutletId(matchedOutlets[0].value);
                fetchCategoriesForOutlet(matchedOutlets[0].value);
                return;
            }
        }

        // Fallback: infer outlet from item or category names (they often contain outlet name)
        if (outletOptions.length > 0) {
            const requiredItems = watch('required_items') || [];
            const selectedCategories = watch('category.categories') || [];

            // Collect all labels to search through
            const labels = [
                ...requiredItems.map((i) => i.label || ''),
                ...selectedCategories.map((c) => c.label || ''),
            ];

            for (const label of labels) {
                if (!label) continue;
                const matchedOutlet = outletOptions.find((o) =>
                    o.label && label.toLowerCase().includes(o.label.toLowerCase())
                );
                if (matchedOutlet) {
                    setSelectedOutlets([matchedOutlet]);
                    setActiveOutletId(matchedOutlet.value);
                    fetchCategoriesForOutlet(matchedOutlet.value);
                    break;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Active outlet data
    const activeData = getOutletData(activeOutletId);
    const categoryTree = activeData.tree;
    const expandedIds = activeData.expandedIds;

    // Filter tree based on search query
    const filteredCategoryTree = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return categoryTree;

        return categoryTree
            .map((category) => {
                const categoryMatch = category.internal_name?.toLowerCase().includes(query);
                const filteredSubs = (category.sub_categories || []).filter((sub) =>
                    sub.internal_name?.toLowerCase().includes(query)
                );

                // In categories mode: show category if it matches or has matching subcategories
                if (mode === 'specific_categories') {
                    if (categoryMatch || filteredSubs.length > 0) {
                        return { ...category, sub_categories: categoryMatch ? category.sub_categories : filteredSubs };
                    }
                    return null;
                }

                // In items mode: also search through loaded items and variants
                const matchingItemCatIds = [];
                const checkItemsMatch = (items) =>
                    items.some((item) => {
                        if (item.internal_name?.toLowerCase().includes(query)) return true;
                        // Also check variants
                        const variants = activeData.itemVariants[item.id] || [];
                        return variants.some((v) => {
                            const vTitle = v?.translations?.title?.[languageId] || '';
                            return vTitle.toLowerCase().includes(query);
                        });
                    });

                const catItems = activeData.categoryItems[category.id] || [];
                if (checkItemsMatch(catItems)) {
                    matchingItemCatIds.push(category.id);
                }
                (category.sub_categories || []).forEach((sub) => {
                    const subItems = activeData.categoryItems[sub.id] || [];
                    if (checkItemsMatch(subItems)) {
                        matchingItemCatIds.push(sub.id);
                    }
                });

                if (categoryMatch || filteredSubs.length > 0 || matchingItemCatIds.length > 0) {
                    return {
                        ...category,
                        sub_categories: categoryMatch
                            ? category.sub_categories
                            : (category.sub_categories || []).filter(
                                  (sub) =>
                                      filteredSubs.some((fs) => fs.id === sub.id) ||
                                      matchingItemCatIds.includes(sub.id)
                              ),
                    };
                }
                return null;
            })
            .filter(Boolean);
    }, [categoryTree, searchQuery, mode, activeData.categoryItems, activeData.itemVariants, languageId]);

    // Filter items within a category based on search query
    const getFilteredItems = useCallback(
        (categoryId) => {
            const items = activeData.categoryItems[categoryId];
            if (!items) return items; // null/undefined — not loaded yet
            const query = searchQuery.toLowerCase().trim();
            if (!query) return items;
            return items.filter((item) => {
                if (item.internal_name?.toLowerCase().includes(query)) return true;
                // Also match if any variant matches
                const variants = activeData.itemVariants[item.id] || [];
                return variants.some((v) => {
                    const vTitle = v?.translations?.title?.[languageId] || '';
                    return vTitle.toLowerCase().includes(query);
                });
            });
        },
        [activeData.categoryItems, activeData.itemVariants, searchQuery, languageId]
    );

    // Fetch items for a category (lazy-load on expand)
    const fetchItemsForCategory = useCallback(
        (categoryId) => {
            if (!activeOutletId) return;
            const data = getOutletData(activeOutletId);
            if (data.categoryItems[categoryId] || loadingItemsFor[categoryId]) return;

            setLoadingItemsFor((prev) => ({ ...prev, [categoryId]: true }));
            APIV3.get(`/restaurants/:restaurantId/outlets/${activeOutletId}/categories/${categoryId}/items/stock`)
                .then((response) => {
                    if (response.success) {
                        setOutletData(activeOutletId, (prev) => ({
                            ...prev,
                            categoryItems: { ...prev.categoryItems, [categoryId]: response.items || [] },
                        }));
                    }
                })
                .catch(() => {})
                .finally(() => {
                    setLoadingItemsFor((prev) => ({ ...prev, [categoryId]: false }));
                });
        },
        [activeOutletId, loadingItemsFor]
    );

    // Fetch variants for an item (lazy-load on expand)
    const fetchVariantsForItem = useCallback(
        (itemId) => {
            if (!activeOutletId) return;
            const data = getOutletData(activeOutletId);
            if (data.itemVariants[itemId] || loadingVariantsFor[itemId]) return;

            setLoadingVariantsFor((prev) => ({ ...prev, [itemId]: true }));
            APIV3.get(`/restaurants/:restaurantId/outlets/${activeOutletId}/items/${itemId}/variant-options/stock`)
                .then((response) => {
                    if (response.success) {
                        setOutletData(activeOutletId, (prev) => ({
                            ...prev,
                            itemVariants: { ...prev.itemVariants, [itemId]: response.variant_options || [] },
                        }));
                    }
                })
                .catch(() => {})
                .finally(() => {
                    setLoadingVariantsFor((prev) => ({ ...prev, [itemId]: false }));
                });
        },
        [activeOutletId, loadingVariantsFor]
    );

    const toggleExpand = useCallback(
        (id, categoryId, itemId) => {
            if (!activeOutletId) return;

            // Update expandedIds for this outlet
            setOutletData(activeOutletId, (prev) => {
                const newExpanded = { ...prev.expandedIds, [id]: !prev.expandedIds[id] };
                return { ...prev, expandedIds: newExpanded };
            });

            // If expanding, trigger lazy-load for items/variants
            const currentExpanded = getOutletData(activeOutletId).expandedIds;
            if (!currentExpanded[id]) {
                if (categoryId) {
                    fetchItemsForCategory(categoryId);
                }
                if (itemId) {
                    fetchVariantsForItem(itemId);
                }
            }
        },
        [activeOutletId, mode, fetchItemsForCategory, fetchVariantsForItem]
    );

    // --- Category mode helpers ---
    const selectedCategories = watch('category.categories') || [];

    const isCategoryChecked = useCallback(
        (categoryId) => {
            return selectedCategories.some((c) => c.required_item_category_id === categoryId);
        },
        [selectedCategories]
    );

    const toggleCategory = useCallback(
        (category, isSubCategory) => {
            const categoryId = category.id;
            const label = category.internal_name || '';

            const current = watch('category.categories') || [];
            const exists = current.some((c) => c.required_item_category_id === categoryId);

            if (exists) {
                const updated = current.filter((c) => c.required_item_category_id !== categoryId);
                setValue('category.categories', updated, { shouldDirty: true });
            } else {
                const newItem = {
                    label,
                    quantity: 1,
                    required_item_category_id: categoryId,
                    isSubCategory,
                };
                setValue('category.categories', [...current, newItem], { shouldDirty: true });
            }
        },
        [setValue, watch]
    );

    // Parent category check state for categories mode (considers subcategories)
    const getParentCategoryCheckState = useCallback(
        (category) => {
            const subCategories = category.sub_categories || [];
            if (subCategories.length === 0) {
                // No subcategories — just check if this category itself is selected
                return { checked: isCategoryChecked(category.id), indeterminate: false };
            }
            // Collect all IDs: parent + all subcategories
            const allIds = [category.id, ...subCategories.map((sub) => sub.id)];
            const selectedCount = allIds.filter((id) => isCategoryChecked(id)).length;
            return {
                checked: selectedCount === allIds.length,
                indeterminate: selectedCount > 0 && selectedCount < allIds.length,
            };
        },
        [isCategoryChecked]
    );

    // Toggle parent category: select/deselect ALL subcategories + parent itself
    const toggleParentCategory = useCallback(
        (category) => {
            const subCategories = category.sub_categories || [];
            if (subCategories.length === 0) {
                // No subcategories — toggle just this category
                toggleCategory(category, false);
                return;
            }

            const current = watch('category.categories') || [];
            const allIds = [category.id, ...subCategories.map((sub) => sub.id)];
            const { checked: allChecked } = getParentCategoryCheckState(category);

            if (allChecked) {
                // Deselect all (parent + subcategories)
                const allIdsSet = new Set(allIds);
                const updated = current.filter((c) => !allIdsSet.has(c.required_item_category_id));
                setValue('category.categories', updated, { shouldDirty: true });
            } else {
                // Select all missing (parent + subcategories)
                const allIdsSet = new Set(allIds);
                const filtered = current.filter((c) => !allIdsSet.has(c.required_item_category_id));
                // Add parent
                filtered.push({
                    label: category.internal_name || '',
                    quantity: 1,
                    required_item_category_id: category.id,
                    isSubCategory: false,
                });
                // Add all subcategories
                subCategories.forEach((sub) => {
                    filtered.push({
                        label: sub.internal_name || '',
                        quantity: 1,
                        required_item_category_id: sub.id,
                        isSubCategory: true,
                    });
                });
                setValue('category.categories', filtered, { shouldDirty: true });
            }
        },
        [watch, setValue, getParentCategoryCheckState, toggleCategory]
    );

    // --- Items mode helpers ---
    const selectedItems = watch('required_items') || [];

    // Helper: get all variant IDs for an item entry (handles both flat and grouped formats)
    const getEntryVariantIds = useCallback((entry) => {
        if (entry.variant_option_ids && Array.isArray(entry.variant_option_ids)) {
            return entry.variant_option_ids;
        }
        if (entry.variant_option_id) {
            return [entry.variant_option_id];
        }
        return [];
    }, []);

    // Check if a specific variant is selected (supports both flat and grouped formats)
    const isVariantChecked = useCallback(
        (itemId, variantOptionId) => {
            return selectedItems.some((i) => {
                if (i.item_id !== itemId) return false;
                // Grouped format: variant_option_ids array
                if (i.variant_option_ids && Array.isArray(i.variant_option_ids)) {
                    return i.variant_option_ids.includes(variantOptionId);
                }
                // Flat format: single variant_option_id
                return i.variant_option_id === variantOptionId;
            });
        },
        [selectedItems]
    );

    // Check item-level state: all variants selected, some, or none
    const getItemCheckState = useCallback(
        (item) => {
            const variants = activeData.itemVariants[item.id] || [];
            if (variants.length === 0) {
                // No variants — simple check if item itself is selected
                const checked = selectedItems.some(
                    (i) => i.item_id === item.id && getEntryVariantIds(i).length === 0
                );
                return { checked, indeterminate: false };
            }
            // Has variants — check how many are selected
            const selectedCount = variants.filter((v) => isVariantChecked(item.id, v.id)).length;
            return {
                checked: selectedCount === variants.length,
                indeterminate: selectedCount > 0 && selectedCount < variants.length,
            };
        },
        [selectedItems, activeData.itemVariants, getEntryVariantIds, isVariantChecked]
    );

    // Toggle a single variant — uses grouped format (one entry per item with variant_option_ids array)
    const toggleVariant = useCallback(
        (item, variantOption) => {
            const itemLabel = item.internal_name || '';
            const current = watch('required_items') || [];
            const existingIndex = current.findIndex((i) => i.item_id === item.id);

            if (existingIndex >= 0) {
                const existing = current[existingIndex];
                const currentVariantIds = getEntryVariantIds(existing);
                const isChecked = currentVariantIds.includes(variantOption.id);

                if (isChecked) {
                    // Remove this variant from the entry
                    const newVariantIds = currentVariantIds.filter((id) => id !== variantOption.id);
                    if (newVariantIds.length === 0 && currentVariantIds.length > 0) {
                        // No variants left — remove the entire entry
                        const updated = current.filter((_, idx) => idx !== existingIndex);
                        setValue('required_items', updated, { shouldDirty: true });
                    } else {
                        // Update the entry with remaining variants
                        const updated = [...current];
                        updated[existingIndex] = {
                            ...existing,
                            variant_option_ids: newVariantIds,
                        };
                        // Clean up old flat format field if present
                        delete updated[existingIndex].variant_option_id;
                        setValue('required_items', updated, { shouldDirty: true });
                    }
                } else {
                    // Add this variant to the entry
                    const updated = [...current];
                    updated[existingIndex] = {
                        ...existing,
                        variant_option_ids: [...currentVariantIds, variantOption.id],
                    };
                    // Clean up old flat format field if present
                    delete updated[existingIndex].variant_option_id;
                    setValue('required_items', updated, { shouldDirty: true });
                }
            } else {
                // No entry for this item yet — create new grouped entry
                const newEntry = {
                    label: itemLabel,
                    item_id: item.id,
                    quantity: 1,
                    variant_option_ids: [variantOption.id],
                };
                setValue('required_items', [...current, newEntry], { shouldDirty: true });
            }
        },
        [getEntryVariantIds, setValue, watch]
    );

    // Toggle an item: if it has variants, select/deselect ALL variants; otherwise toggle the item itself
    const toggleItem = useCallback(
        (item) => {
            const itemLabel = item.internal_name || '';
            const current = watch('required_items') || [];
            const variants = activeData.itemVariants[item.id] || [];

            if (variants.length > 0) {
                // Has variants — toggle all variants as ONE grouped entry
                const { checked: allChecked } = getItemCheckState(item);

                if (allChecked) {
                    // Deselect — remove the item entry entirely
                    const updated = current.filter((i) => i.item_id !== item.id);
                    setValue('required_items', updated, { shouldDirty: true });
                } else {
                    // Select all variants — create/replace with one grouped entry
                    const filtered = current.filter((i) => i.item_id !== item.id);
                    filtered.push({
                        label: itemLabel,
                        item_id: item.id,
                        quantity: 1,
                        variant_option_ids: variants.map((v) => v.id),
                    });
                    setValue('required_items', filtered, { shouldDirty: true });
                }
            } else {
                // No variants — toggle the item itself
                const exists = current.some(
                    (i) => i.item_id === item.id && getEntryVariantIds(i).length === 0
                );
                if (exists) {
                    const updated = current.filter(
                        (i) => !(i.item_id === item.id && getEntryVariantIds(i).length === 0)
                    );
                    setValue('required_items', updated, { shouldDirty: true });
                } else {
                    const newEntry = {
                        label: itemLabel,
                        item_id: item.id,
                        quantity: 1,
                    };
                    setValue('required_items', [...current, newEntry], { shouldDirty: true });
                }
            }
        },
        [setValue, watch, activeData.itemVariants, getItemCheckState, getEntryVariantIds]
    );

    // Build the grouped selection entry for an item (with variants if available)
    const buildItemEntry = useCallback(
        (item) => {
            const itemLabel = item.internal_name || '';
            const variants = activeData.itemVariants[item.id] || [];

            if (variants.length > 0) {
                // Item has variants — one grouped entry with all variant IDs
                return {
                    label: itemLabel,
                    item_id: item.id,
                    quantity: 1,
                    variant_option_ids: variants.map((v) => v.id),
                };
            }
            // No variants — just the item
            return { label: itemLabel, item_id: item.id, quantity: 1 };
        },
        [activeData.itemVariants]
    );

    // Check if all items (and their variants) in a category are fully selected
    const areAllCategoryItemsChecked = useCallback(
        (categoryId) => {
            const items = activeData.categoryItems[categoryId] || [];
            if (items.length === 0) return false;
            return items.every((item) => {
                const { checked } = getItemCheckState(item);
                return checked;
            });
        },
        [activeData.categoryItems, getItemCheckState]
    );

    // Check if some (but not all) items in a category are selected
    const areSomeCategoryItemsChecked = useCallback(
        (categoryId) => {
            const items = activeData.categoryItems[categoryId] || [];
            if (items.length === 0) return false;
            const checkedCount = items.filter((item) => {
                const { checked, indeterminate } = getItemCheckState(item);
                return checked || indeterminate;
            }).length;
            return checkedCount > 0 && checkedCount < items.length;
        },
        [activeData.categoryItems, getItemCheckState]
    );

    // Toggle all items (with variants) in a category/subcategory
    const toggleAllCategoryItems = useCallback(
        (categoryId) => {
            const items = activeData.categoryItems[categoryId] || [];
            if (items.length === 0) return;

            const current = watch('required_items') || [];
            const allChecked = areAllCategoryItemsChecked(categoryId);

            if (allChecked) {
                // Uncheck all items in this category
                const categoryItemIds = new Set(items.map((item) => item.id));
                const updated = current.filter((si) => !categoryItemIds.has(si.item_id));
                setValue('required_items', updated, { shouldDirty: true });
            } else {
                // Check all items — one grouped entry per item
                const categoryItemIds = new Set(items.map((item) => item.id));
                const filtered = current.filter((si) => !categoryItemIds.has(si.item_id));
                items.forEach((item) => {
                    filtered.push(buildItemEntry(item));
                });
                setValue('required_items', filtered, { shouldDirty: true });
            }
        },
        [activeData.categoryItems, areAllCategoryItemsChecked, buildItemEntry, setValue, watch]
    );

    // For parent categories: check across all subcategories + direct items
    const areAllParentCategoryItemsChecked = useCallback(
        (category) => {
            const allCatIds = [category.id];
            (category.sub_categories || []).forEach((sub) => allCatIds.push(sub.id));

            let totalItems = 0;
            let fullyChecked = 0;
            let partiallyChecked = 0;
            allCatIds.forEach((catId) => {
                const items = activeData.categoryItems[catId] || [];
                totalItems += items.length;
                items.forEach((item) => {
                    const { checked, indeterminate } = getItemCheckState(item);
                    if (checked) fullyChecked++;
                    if (indeterminate) partiallyChecked++;
                });
            });

            return {
                allChecked: totalItems > 0 && fullyChecked === totalItems,
                someChecked: (fullyChecked > 0 || partiallyChecked > 0) && fullyChecked < totalItems,
            };
        },
        [activeData.categoryItems, getItemCheckState]
    );

    const toggleAllParentCategoryItems = useCallback(
        (category) => {
            const allCatIds = [category.id];
            (category.sub_categories || []).forEach((sub) => allCatIds.push(sub.id));

            const allItems = [];
            allCatIds.forEach((catId) => {
                (activeData.categoryItems[catId] || []).forEach((item) => allItems.push(item));
            });

            if (allItems.length === 0) return;

            const current = watch('required_items') || [];
            const { allChecked } = areAllParentCategoryItemsChecked(category);

            if (allChecked) {
                const allItemIds = new Set(allItems.map((item) => item.id));
                const updated = current.filter((si) => !allItemIds.has(si.item_id));
                setValue('required_items', updated, { shouldDirty: true });
            } else {
                // Remove partial selections, then add one grouped entry per item
                const allItemIds = new Set(allItems.map((item) => item.id));
                const filtered = current.filter((si) => !allItemIds.has(si.item_id));
                allItems.forEach((item) => {
                    filtered.push(buildItemEntry(item));
                });
                setValue('required_items', filtered, { shouldDirty: true });
            }
        },
        [activeData.categoryItems, areAllParentCategoryItemsChecked, buildItemEntry, setValue, watch]
    );

    // --- Render helpers ---
    const getVariantLabel = (variant) => {
        return variant?.translations?.title?.[languageId] || `Variant #${variant.id}`;
    };

    const renderVariants = (item) => {
        const variants = activeData.itemVariants[item.id];
        if (!variants || variants.length === 0) {
            if (loadingVariantsFor[item.id]) {
                return (
                    <div style={{ paddingLeft: 48, paddingTop: 4 }}>
                        <EuiLoadingSpinner size='s' />
                    </div>
                );
            }
            return null;
        }
        return variants.map((variant) => (
            <div key={`variant_${variant.id}`} style={{ paddingLeft: 48, paddingTop: 4, paddingBottom: 4 }}>
                {mode === 'specific_items' ? (
                    <EuiCheckbox
                        id={`variant_${item.id}_${variant.id}`}
                        label={
                            <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                                <EuiFlexItem grow={false}>
                                    <EuiText size='xs'>{getVariantLabel(variant)}</EuiText>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false}>
                                    <EuiBadge color='hollow'>Variant</EuiBadge>
                                </EuiFlexItem>
                            </EuiFlexGroup>
                        }
                        checked={isVariantChecked(item.id, variant.id)}
                        onChange={() => toggleVariant(item, variant)}
                        disabled={isLoadingAllData}
                    />
                ) : (
                    <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                        <EuiFlexItem grow={false}>
                            <EuiText size='xs' color='subdued'>{getVariantLabel(variant)}</EuiText>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiBadge color='hollow'>Variant</EuiBadge>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                )}
            </div>
        ));
    };

    const renderItems = (categoryId) => {
        const items = getFilteredItems(categoryId);
        if (!items) {
            // Items not loaded yet — show spinner (bulk or individual loading)
            return (
                <div style={{ paddingLeft: 32, paddingTop: 8 }}>
                    <EuiLoadingSpinner size='s' />
                    <EuiText size='xs' color='subdued' style={{ display: 'inline', marginLeft: 8 }}>
                        Loading items...
                    </EuiText>
                </div>
            );
        }
        if (items.length === 0) {
            return (
                <div style={{ paddingLeft: 32, paddingTop: 4 }}>
                    <EuiText size='xs' color='subdued'>
                        No items in this category
                    </EuiText>
                </div>
            );
        }
        return items.map((item) => {
            // Use actual loaded variants count (not API's variants_count which may be inaccurate)
            const loadedVariants = activeData.itemVariants[item.id] || [];
            const actualVariantCount = loadedVariants.length > 0 ? loadedVariants.length : item.variants_count;
            const hasVariants = actualVariantCount > 0;
            const itemExpandId = `item_${item.id}`;
            const isExpanded = expandedIds[itemExpandId];
            const { checked: itemChecked, indeterminate: itemIndeterminate } = getItemCheckState(item);

            return (
                <div key={`item_${item.id}`} style={{ paddingLeft: 32, paddingTop: 4, paddingBottom: 4 }}>
                    <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                        {hasVariants && (
                            <EuiFlexItem grow={false}>
                                <EuiButtonIcon
                                    iconType={isExpanded ? 'arrowDown' : 'arrowRight'}
                                    onClick={() => toggleExpand(itemExpandId, null, item.id)}
                                    aria-label='Toggle variants'
                                    size='s'
                                />
                            </EuiFlexItem>
                        )}
                        <EuiFlexItem grow={false}>
                            {mode === 'specific_items' ? (
                                <EuiCheckbox
                                    id={`item_${item.id}`}
                                    label={
                                        <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                                            <EuiFlexItem grow={false}>
                                                <EuiText size='s'>{item.internal_name}</EuiText>
                                            </EuiFlexItem>
                                            {hasVariants && (
                                                <EuiFlexItem grow={false}>
                                                    <EuiBadge color='primary'>
                                                        {actualVariantCount} variant{actualVariantCount > 1 ? 's' : ''}
                                                    </EuiBadge>
                                                </EuiFlexItem>
                                            )}
                                        </EuiFlexGroup>
                                    }
                                    checked={itemChecked}
                                    indeterminate={itemIndeterminate}
                                    onChange={() => toggleItem(item)}
                                    disabled={isLoadingAllData}
                                />
                            ) : (
                                <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                                    <EuiFlexItem grow={false}>
                                        <EuiText size='s' color='subdued'>{item.internal_name}</EuiText>
                                    </EuiFlexItem>
                                    {hasVariants && (
                                        <EuiFlexItem grow={false}>
                                            <EuiBadge color='primary'>
                                                {actualVariantCount} variant{actualVariantCount > 1 ? 's' : ''}
                                            </EuiBadge>
                                        </EuiFlexItem>
                                    )}
                                </EuiFlexGroup>
                            )}
                        </EuiFlexItem>
                    </EuiFlexGroup>
                    {hasVariants && isExpanded && renderVariants(item)}
                </div>
            );
        });
    };

    const renderSubcategory = (subCategory) => {
        const subId = `sub_${subCategory.id}`;
        const isExpanded = expandedIds[subId];

        // Unified tree: always show expandable subcategory with items underneath
        // In specific_categories mode: subcategory checkbox adds to category.categories
        // In specific_items mode: subcategory checkbox toggles all items underneath
        const isCategoriesMode = mode === 'specific_categories';
        const allItemsChecked = areAllCategoryItemsChecked(subCategory.id);
        const someItemsChecked = areSomeCategoryItemsChecked(subCategory.id);

        return (
            <div key={subId} style={{ paddingLeft: 24, paddingTop: 4, paddingBottom: 4 }}>
                <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                    <EuiFlexItem grow={false}>
                        <EuiButtonIcon
                            iconType={isExpanded ? 'arrowDown' : 'arrowRight'}
                            onClick={() => toggleExpand(subId, subCategory.id, null)}
                            aria-label='Toggle subcategory'
                            size='s'
                        />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiCheckbox
                            id={`sub_check_${subCategory.id}`}
                            label={
                                <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                                    <EuiFlexItem grow={false}>
                                        <EuiText size='s'>
                                            <strong>{subCategory.internal_name}</strong>
                                        </EuiText>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false}>
                                        <EuiBadge color='hollow'>Sub Category</EuiBadge>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            }
                            checked={isCategoriesMode ? isCategoryChecked(subCategory.id) : allItemsChecked}
                            indeterminate={isCategoriesMode ? false : !allItemsChecked && someItemsChecked}
                            onChange={() =>
                                isCategoriesMode
                                    ? toggleCategory(subCategory, true)
                                    : toggleAllCategoryItems(subCategory.id)
                            }
                            disabled={isLoadingAllData}
                        />
                    </EuiFlexItem>
                </EuiFlexGroup>
                {isExpanded && renderItems(subCategory.id)}
            </div>
        );
    };

    const renderCategory = (category) => {
        const catId = `cat_${category.id}`;
        const isExpanded = expandedIds[catId];
        const hasSubCategories = category.sub_categories && category.sub_categories.length > 0;

        // Unified tree: always show expandable category with full hierarchy
        const isCategoriesMode = mode === 'specific_categories';
        const { allChecked: parentAllChecked, someChecked: parentSomeChecked } =
            areAllParentCategoryItemsChecked(category);

        // For categories mode: parent checkbox reflects subcategory selection state
        const catCheckState = isCategoriesMode
            ? getParentCategoryCheckState(category)
            : { checked: parentAllChecked, indeterminate: !parentAllChecked && parentSomeChecked };

        return (
            <div key={catId} style={{ paddingTop: 6, paddingBottom: 6 }}>
                <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                    <EuiFlexItem grow={false}>
                        <EuiButtonIcon
                            iconType={isExpanded ? 'arrowDown' : 'arrowRight'}
                            onClick={() => toggleExpand(catId, category.id, null)}
                            aria-label='Toggle category'
                            size='s'
                        />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiCheckbox
                            id={`cat_check_${category.id}`}
                            label={
                                <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                                    <EuiFlexItem grow={false}>
                                        <EuiText>
                                            <strong>{category.internal_name}</strong>
                                        </EuiText>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false}>
                                        <EuiBadge color='default'>Category</EuiBadge>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            }
                            checked={catCheckState.checked}
                            indeterminate={catCheckState.indeterminate}
                            onChange={() =>
                                isCategoriesMode
                                    ? toggleParentCategory(category)
                                    : toggleAllParentCategoryItems(category)
                            }
                            disabled={isLoadingAllData}
                        />
                    </EuiFlexItem>
                </EuiFlexGroup>
                {isExpanded && (
                    <>
                        {hasSubCategories &&
                            category.sub_categories.map((sub) => renderSubcategory(sub))}
                        {!hasSubCategories && renderItems(category.id)}
                    </>
                )}
            </div>
        );
    };

    // Count selections — for grouped format, count total individual items+variants
    const selectionCount = useMemo(() => {
        if (mode === 'specific_categories') return selectedCategories.length;
        // Count: items without variants as 1, items with variants as count of variant IDs
        let count = 0;
        selectedItems.forEach((entry) => {
            const variantIds = getEntryVariantIds(entry);
            count += variantIds.length > 0 ? variantIds.length : 1;
        });
        return count;
    }, [mode, selectedCategories, selectedItems, getEntryVariantIds]);

    return (
        <EuiFlyout
            onClose={onClose}
            ownFocus
            size='l'
            aria-labelledby='categoryItemSelectionFlyoutTitle'
        >
            <EuiFlyoutHeader hasBorder>
                <EuiFlexGroup alignItems='center' gutterSize='s'>
                    <EuiFlexItem grow={false}>
                        <EuiTitle size='m'>
                            <h2 id='categoryItemSelectionFlyoutTitle'>
                                {mode === 'specific_categories' ? 'Select Categories' : 'Select Items'}
                            </h2>
                        </EuiTitle>
                    </EuiFlexItem>
                    {selectionCount > 0 && (
                        <EuiFlexItem grow={false}>
                            <EuiBadge color='primary'>{selectionCount} selected</EuiBadge>
                        </EuiFlexItem>
                    )}
                </EuiFlexGroup>
            </EuiFlyoutHeader>

            <EuiFlyoutBody>
                {/* Step 1: Multi-Outlet Selection */}
                <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
                    <EuiFlexItem grow={false}>
                        <EuiText size='s'>
                            <strong>Select Outlets</strong>
                        </EuiText>
                    </EuiFlexItem>
                    {selectedOutlets.length > 0 && (
                        <EuiFlexItem grow={false}>
                            <EuiBadge color='primary'>{selectedOutlets.length} selected</EuiBadge>
                        </EuiFlexItem>
                    )}
                </EuiFlexGroup>
                <EuiSpacer size='s' />
                <EuiComboBox
                    aria-label='Select outlets'
                    placeholder='Search and select outlets'
                    options={outletOptions}
                    selectedOptions={selectedOutlets}
                    onChange={handleOutletChange}
                    isClearable
                    fullWidth
                />

                {selectedOutlets.length === 0 && (
                    <>
                        <EuiSpacer size='m' />
                        <EuiCallOut
                            title='Please select at least one outlet to proceed'
                            color='warning'
                            iconType='alert'
                            size='s'
                        />
                    </>
                )}

                {/* Outlet Pills */}
                {selectedOutlets.length > 0 && (
                    <>
                        <EuiSpacer size='m' />
                        <EuiFlexGroup gutterSize='s' wrap responsive={false}>
                            {selectedOutlets.map((outlet) => (
                                <EuiFlexItem grow={false} key={outlet.value}>
                                    <EuiBadge
                                        color={activeOutletId === outlet.value ? 'primary' : 'hollow'}
                                        onClick={() => handleOutletPillClick(outlet.value)}
                                        onClickAriaLabel={`View ${outlet.label}`}
                                        iconType='cross'
                                        iconSide='right'
                                        iconOnClick={() => handleRemoveOutlet(outlet.value)}
                                        iconOnClickAriaLabel={`Remove ${outlet.label}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {outlet.label}
                                    </EuiBadge>
                                </EuiFlexItem>
                            ))}
                        </EuiFlexGroup>
                    </>
                )}

                <EuiHorizontalRule />

                {/* Step 2: Tree for active outlet */}
                {!activeOutletId && selectedOutlets.length > 0 && (
                    <EuiCallOut
                        title='Click on an outlet pill above to view its categories'
                        color='primary'
                        iconType='iInCircle'
                        size='s'
                    />
                )}

                {isLoadingCategories && (
                    <EuiFlexGroup justifyContent='center'>
                        <EuiFlexItem grow={false}>
                            <EuiLoadingSpinner size='l' />
                        </EuiFlexItem>
                    </EuiFlexGroup>
                )}

                {activeOutletId && !isLoadingCategories && categoryTree.length === 0 && (
                    <EuiCallOut
                        title='No categories found for this outlet'
                        color='primary'
                        iconType='iInCircle'
                        size='s'
                    />
                )}

                {activeOutletId && !isLoadingCategories && categoryTree.length > 0 && (
                    <>
                        <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                            <EuiFlexItem grow={false}>
                                <EuiText size='xs' color='subdued'>
                                    Showing categories for:{' '}
                                    <strong>{selectedOutlets.find((o) => o.value === activeOutletId)?.label}</strong>
                                </EuiText>
                            </EuiFlexItem>
                            {isLoadingAllData && (
                                <EuiFlexItem grow={false}>
                                    <EuiFlexGroup alignItems='center' gutterSize='xs' responsive={false}>
                                        <EuiFlexItem grow={false}>
                                            <EuiLoadingSpinner size='s' />
                                        </EuiFlexItem>
                                        <EuiFlexItem grow={false}>
                                            <EuiText size='xs' color='subdued'>Loading items...</EuiText>
                                        </EuiFlexItem>
                                    </EuiFlexGroup>
                                </EuiFlexItem>
                            )}
                        </EuiFlexGroup>
                        <EuiSpacer size='s' />

                        {/* Local Search */}
                        <EuiFieldSearch
                            placeholder='Search categories, items, variants...'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            isClearable
                            fullWidth
                        />
                        <EuiSpacer size='s' />

                        {filteredCategoryTree.length === 0 && searchQuery && (
                            <EuiCallOut
                                title={`No results found for "${searchQuery}"`}
                                color='primary'
                                iconType='iInCircle'
                                size='s'
                            />
                        )}

                        {filteredCategoryTree.map((category) => renderCategory(category))}
                    </>
                )}
            </EuiFlyoutBody>

            <EuiFlyoutFooter>
                <EuiFlexGroup justifyContent='spaceBetween'>
                    <EuiFlexItem grow={false}>
                        <EuiButtonEmpty iconType='cross' onClick={onClose} flush='left'>
                            Close
                        </EuiButtonEmpty>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButton onClick={onClose} fill>
                            Done {selectionCount > 0 ? `(${selectionCount})` : ''}
                        </EuiButton>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFlyoutFooter>
        </EuiFlyout>
    );
};

export default React.memo(CategoryItemSelectionFlyout);
